import React from 'react';
import * as MuiIcons from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import StarIcon from '@mui/icons-material/Star';
import InfoIcon from '@mui/icons-material/Info';
import MailIcon from '@mui/icons-material/Mail';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SegmentIcon from '@mui/icons-material/Segment';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupIcon from '@mui/icons-material/Group';
import SourceIcon from '@mui/icons-material/Source';
import GridViewIcon from '@mui/icons-material/GridView';
import AddHomeIcon from '@mui/icons-material/AddHome';

/** PascalCase to snake_case for storage/API (e.g. CalendarMonth -> calendar_month) */
function toSnakeCase(str: string): string {
  return str
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '');
}

/** Human-readable label from snake_case (e.g. calendar_month -> Calendar month) */
export function toLabel(snake: string): string {
  return snake
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const EXCLUDE_KEYS = new Set(['default', '__esModule']);

export interface MuiIconOption {
  value: string;
  label: string;
  Icon: React.ComponentType<{ sx?: object; fontSize?: 'small' | 'medium' | 'large' }>;
}

type IconComponent = React.ComponentType<{ sx?: object; fontSize?: 'small' | 'medium' | 'large' }>;

const FALLBACK_ICONS: [string, IconComponent][] = [
  ['home', HomeIcon],
  ['menu', MenuIcon],
  ['settings', SettingsIcon],
  ['dashboard', DashboardIcon],
  ['folder', FolderIcon],
  ['star', StarIcon],
  ['info', InfoIcon],
  ['mail', MailIcon],
  ['people', PeopleIcon],
  ['chat', ChatIcon],
  ['calendar_month', CalendarMonthIcon],
  ['account_tree', AccountTreeIcon],
  ['segment', SegmentIcon],
  ['storage', StorageIcon],
  ['security', SecurityIcon],
  ['manage_accounts', ManageAccountsIcon],
  ['assessment', AssessmentIcon],
  ['group', GroupIcon],
  ['source', SourceIcon],
  ['apps', GridViewIcon],
  ['add_home', AddHomeIcon],
];

function buildFallbackList(): MuiIconOption[] {
  return FALLBACK_ICONS.map(([value, Icon]) => ({
    value,
    label: toLabel(value),
    Icon,
  })).sort((a, b) => a.label.localeCompare(b.label));
}

let cachedList: MuiIconOption[] | null = null;
let cachedMap: Record<string, IconComponent> | null = null;

function buildLists(): {
  list: MuiIconOption[];
  map: Record<string, IconComponent>;
} {
  if (cachedList && cachedMap) return { list: cachedList, map: cachedMap };
  const list: MuiIconOption[] = [];
  const map: Record<string, IconComponent> = {};
  const raw = MuiIcons as Record<string, unknown>;
  const source: Record<string, unknown> = raw.default && typeof raw.default === 'object' ? (raw.default as Record<string, unknown>) : raw;
  Object.entries(source).forEach(([key, Icon]) => {
    if (EXCLUDE_KEYS.has(key)) return;
    if (!Icon) return;
    const isComponent =
      typeof Icon === 'function' ||
      (typeof Icon === 'object' && Icon !== null && ('$$typeof' in (Icon as object) || 'render' in (Icon as object)));
    if (!isComponent) return;
    if (!/^[A-Z]/.test(key)) return;
    const value = toSnakeCase(key);
    const label = toLabel(value);
    const IconComponent = Icon as IconComponent;
    list.push({ value, label, Icon: IconComponent });
    map[value] = IconComponent;
  });
  list.sort((a, b) => a.label.localeCompare(b.label));
  cachedList = list.length > 0 ? list : buildFallbackList();
  cachedMap = list.length > 0 ? map : (Object.fromEntries(FALLBACK_ICONS.map(([v, Ic]) => [v, Ic])) as Record<string, IconComponent>);
  return { list: cachedList, map: cachedMap };
}

/** All MUI icons for picker: { value (snake_case), label, Icon } */
export function getAllMuiIcons(): MuiIconOption[] {
  return buildLists().list;
}

/** Resolve icon name (snake_case) to React element; use for display when name may be any MUI icon */
export function getMuiIconComponent(
  iconName: string,
  sx?: object
): React.ReactElement | null {
  if (!iconName) return null;
  const { map } = buildLists();
  const Icon = map[iconName];
  if (!Icon) return null;
  return React.createElement(Icon, sx ? { sx } : {});
}
